import { c as _c } from "react/compiler-runtime";
import { CurrencyEnum, cents, centsToUnits } from '@systemeio/ui';
import { BarElement, CategoryScale, ChartData, Chart as ChartJS, Legend, LinearScale, ScatterDataPoint, Title, Tooltip } from 'chart.js';
import { AffiliateCharInterface, AffiliateStatisticCommonInterface } from 'modules/affiliate-program/affiliate/types/affiliate-interface';
import { getAffiliateChartColor, getAffiliateChartTextColor } from 'modules/affiliate-program/affiliate/utils/get-affiliate-chart-color';
import { getAffiliateChartOptions } from 'modules/affiliate-program/affiliate/utils/get-affiliate-chart-options';
import { getChartValue } from 'modules/affiliate-program/affiliate/utils/get-chart-value';
import { getSortedChartCurrencies } from 'modules/affiliate-program/affiliate/utils/get-sorted-chart-currencies';
import { hasChartValues } from 'modules/affiliate-program/affiliate/utils/has-chart-values';
import { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { CurrencyNameEnum } from 'shared/enums/currency-enum';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { currencyFormat } from 'shared/utils/currency-format';
import { toShortDate } from 'shared/utils/date-time-format';
interface AffiliateChartProps {
  chartData?: AffiliateCharInterface;
  selectedCurrency?: CurrencyEnum;
  affiliateStatistic?: AffiliateStatisticCommonInterface;
}
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
const AffiliateChart = t0 => {
  const $ = _c(40);
  const {
    chartData,
    selectedCurrency,
    affiliateStatistic
  } = t0;
  const {
    user,
    intlLocale
  } = useUser();
  const {
    t
  } = useLocoTranslation();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      labels: [],
      datasets: []
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [data, setData] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [allCurrencies, setAllCurrencies] = useState(t2);
  let t3;
  let t4;
  if ($[2] !== affiliateStatistic) {
    t3 = () => {
      if (affiliateStatistic) {
        setAllCurrencies(Object.keys(affiliateStatistic.total) as CurrencyEnum[]);
      }
    };
    t4 = [affiliateStatistic];
    $[2] = affiliateStatistic;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const [currencies, setCurrencies] = useState(t5);
  let t6;
  let t7;
  if ($[6] !== affiliateStatistic || $[7] !== allCurrencies || $[8] !== selectedCurrency) {
    t6 = () => {
      if (!affiliateStatistic) {
        return;
      }
      if (selectedCurrency) {
        setCurrencies([selectedCurrency]);
      } else {
        setCurrencies(allCurrencies);
      }
    };
    t7 = [affiliateStatistic, allCurrencies, selectedCurrency];
    $[6] = affiliateStatistic;
    $[7] = allCurrencies;
    $[8] = selectedCurrency;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[11] !== allCurrencies || $[12] !== chartData || $[13] !== currencies || $[14] !== intlLocale || $[15] !== t) {
    t8 = () => {
      if (chartData) {
        setData({
          labels: Object.keys(chartData.days).map(el => toShortDate(el, intlLocale)),
          datasets: getSortedChartCurrencies(currencies, chartData.sums).map(cur => ({
            data: Object.keys(chartData.days).map(el_0 => getChartValue(chartData.days[el_0]?.[cur] ?? cents(0)) / 100),
            label: t(CurrencyNameEnum[cur]),
            borderRadius: 2,
            backgroundColor: getAffiliateChartColor(cur, allCurrencies)
          }))
        });
      }
    };
    t9 = [chartData, currencies, allCurrencies, t, intlLocale];
    $[11] = allCurrencies;
    $[12] = chartData;
    $[13] = currencies;
    $[14] = intlLocale;
    $[15] = t;
    $[16] = t8;
    $[17] = t9;
  } else {
    t8 = $[16];
    t9 = $[17];
  }
  useEffect(t8, t9);
  let t10;
  if ($[18] !== t) {
    t10 = t("dashboard.affiliate.commission_earned");
    $[18] = t;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = `${t10}:`;
  let t12;
  if ($[20] !== t11) {
    t12 = <span>{t11}</span>;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== affiliateStatistic || $[23] !== allCurrencies || $[24] !== chartData || $[25] !== currencies || $[26] !== user?.dashboardLocale) {
    t13 = affiliateStatistic && chartData ? <ul className="flex gap-3">{getSortedChartCurrencies(currencies, chartData.sums).map(cur_0 => <li key={cur_0} style={getAffiliateChartTextColor(cur_0, allCurrencies)}>{currencyFormat(centsToUnits(getChartValue(chartData.sums[cur_0] ?? cents(0))), cur_0, user?.dashboardLocale, true)}</li>)}</ul> : <span className="bg-skeleton-primary animate-pulse rounded-lg px-5 text-transparent">.</span>;
    $[22] = affiliateStatistic;
    $[23] = allCurrencies;
    $[24] = chartData;
    $[25] = currencies;
    $[26] = user?.dashboardLocale;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t12 || $[29] !== t13) {
    t14 = <div className="text-text-primary bg-brand-primary-subtle flex gap-1 px-6 py-4 text-[17px] font-bold">{t12}{t13}</div>;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== chartData || $[32] !== currencies || $[33] !== data || $[34] !== selectedCurrency || $[35] !== user?.dashboardLocale) {
    t15 = <div className="px-6 py-4">{chartData ? <Bar options={getAffiliateChartOptions(hasChartValues(currencies, chartData.sums), selectedCurrency, user?.dashboardLocale)} data={data} height={430} /> : <div className="bg-skeleton-primary h-[430px] animate-pulse rounded-lg text-transparent ">.</div>}</div>;
    $[31] = chartData;
    $[32] = currencies;
    $[33] = data;
    $[34] = selectedCurrency;
    $[35] = user?.dashboardLocale;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t14 || $[38] !== t15) {
    t16 = <div className="bg-background-secondary flex h-fit w-full flex-col justify-between overflow-x-hidden rounded-lg">{t14}{t15}</div>;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
};
export default AffiliateChart;